// Every tunable value lives here. If you are about to type a literal into
// application code, name it in this file instead.

export const APP_ROOT_SELECTOR = '#app';
/** The die in the cue. The die on the stage starts its roll from here. */
export const ROLL_BUTTON_SELECTOR = '.cue__die';
/** The box on the stage where the die comes to rest. A coin of energy leaves from it. */
export const STAGE_DIE_SELECTOR = '.stage__die';
/**
 * The player reads "cast" where the code says roll. Rob, 2026-10-12: "wherever we
 * currently say \"roll\" let's make that \"cast\" which doubles for a throw of the dice
 * and casting a spell". The names in the code stay.
 */
export const ROLL_BUTTON_LABEL = 'Cast the die';

/**
 * The name the player sees. Rob, 2026-10-12: "rename the visible name of the
 * game to \"Chesscaster\"". It was Chessnut for a day.
 */
export const GAME_NAME = 'Chesscaster';
export const STORAGE_KEY_THEME = 'arcane:theme';
export const STORAGE_KEY_SETTINGS = 'arcane:settings';
export const STORAGE_KEY_MATCH = 'arcane:match';
/** Raise this when a saved match from older code must not load. */
export const MATCH_SAVE_VERSION = 40;

// ---- Board -----------------------------------------------------------------
export const BOARD_SIZE = 8;
export const SQUARE_COUNT = BOARD_SIZE * BOARD_SIZE;
export const SQUARE_PX = 100;

// ---- Dither ----------------------------------------------------------------
/** The mono look has no gray. A tone is a dither of this many cells across one square. */
export const DITHER_CELLS_PER_SQUARE = 24;
export const DITHER_CELL_PX = SQUARE_PX / DITHER_CELLS_PER_SQUARE;
/** How many of the four cells in a tile are on. `mono.css` reads these ids. */
export const DITHER_TONES = [
  { id: 'dither-quarter', cellsOn: 1 },
  { id: 'dither-half', cellsOn: 2 },
  { id: 'dither-three-quarter', cellsOn: 3 },
] as const;
/** The cells of the band that crosses a royal piece, in the color drawn on each side. */
/** The half dither of the second part of a caster symbol in the mono look. `mono.css` names it too. */
export const DITHER_CASTER_MARK_ID = 'dither-caster-mark';

export const DITHER_GLINT_IDS = {
  gold: 'dither-glint-gold',
  violet: 'dither-glint-violet',
} as const;
/** The cells that cover a stopped piece. The color of its side shows between them. */
export const DITHER_STOPPED_IDS = {
  gold: 'dither-stopped-gold',
  violet: 'dither-stopped-violet',
} as const;
/** The lines that cover an uncapturable piece. The color of its side shows between them. */
export const DITHER_UNCAPTURABLE_IDS = {
  gold: 'dither-uncapturable-gold',
  violet: 'dither-uncapturable-violet',
} as const;
/**
 * The dither of a puff of the cloud a removed piece leaves, as the puff thins: half or a
 * quarter of the cells, in the color of the owner of the piece, with no ground.
 */
export const puffDitherId = (owner: string, paint: 'half' | 'quarter'): string =>
  `dither-puff-${owner}-${paint}`;

// ---- Marks on a square -------------------------------------------------------
/** Every color a mark on a square can have. `board.css` gives each one its color. */
export const MARK_TONES = [
  'ally',
  'enemy',
  'any',
  'board',
  'capture',
  'system',
  'violet',
  'select',
  'danger',
  'must',
  'picked',
  'trace',
  'careful',
  'aggro',
  'deep',
] as const;
/** The width of one band of a mark, in dither cells. */
export const MARK_BAND_CELLS = 1.5;
/** A dashed band: the cells on, then the cells off. */
export const MARK_DASH_CELLS = [3, 2] as const;
/** A cross on a piece that a pick removes: the reach of each arm from the middle, and the width of the arm. */
export const CROSS_REACH_PX = 20;
export const CROSS_WIDTH_PX = DITHER_CELL_PX * 2;
/** The dither of a band: half or a quarter of the cells, in the color of the tone, with no ground. */
export const markDitherId = (tone: string, paint: 'half' | 'quarter'): string =>
  `dither-mark-${tone}-${paint}`;

// ---- Spells ----------------------------------------------------------------
export const WHEEL_SIZE = 8;
/**
 * The faces of the wheel by who the spell is for. A match with a wheel deals
 * this many of each. Until 2026-10-13 the four were buff, debuff, backfire,
 * and boardwide, with 4, 3, 0, and 1.
 */
export const WHEEL_QUOTA = { ally: 4, enemy: 2, any: 1, board: 1 } as const;

/**
 * A roll costs energy. Added 2026-10-12. Rob: "rolls will cost energy and that
 * cost will increase with every roll. So 1 to start, then 2, then 3, then 4,
 * etc. The player will gain 1 energy on every turn, including the first turn."
 */
export const ENERGY_PER_TURN = 1;
/** Godsmode: the energy a rook holds at most. Rob, 2026-10-13: "the rooks, which each hold 3 energy". */
export const ROOK_ENERGY_CAP = 3;
/** Godsmode: the energy a king holds at most. Rob, 2026-10-15: "The king should also carry 2 energy, in the same way that the rooks carry 3 each." */
export const KING_ENERGY_CAP = 2;
/**
 * Godsmode: the energy a capture gives the side of the captor, with the energy
 * the taken piece held. Garrett, 2026-10-15, so that a fight pays for spells.
 */
export const CAPTURE_ENERGY_GAIN = 1;
/** Godsmode: the pips in the energy row of a caster. Energy over this shows in the count alone. */
export const ENERGY_PIP_COUNT = 6;
/** The time from one pip that fills to the next, so the energy of a roll comes one point at a time. */
export const ENERGY_PIP_STEP_MS = 110;
/**
 * Godsmode: each energy of a roll flies from the die to the row of pips as a
 * coin. Garrett, 2026-10-12: "a little energy moudle that comes out of the
 * die and goes bloop into the energy reserve hole through an animated arc".
 * The die rests for this long before the first coin leaves it.
 */
export const ENERGY_FLIGHT_REST_MS = 200;
/** The second coin leaves the die this long after the first, so both are in the air. */
export const ENERGY_FLIGHT_STAGGER_MS = 180;
/** How long one coin flies. Each flight draws from the range. */
export const ENERGY_FLIGHT_MS = [380, 460] as const;
/** A coin lands at least this long after the coin before it, so two drops are two sounds. */
export const ENERGY_FLIGHT_LAND_GAP_MS = 140;
/** How high the arc of a coin goes over the straight line, as a part of the length of that line. */
export const ENERGY_FLIGHT_BEND = [0.22, 0.4] as const;
/** The coin starts slow and drops fast. One is a steady flight. */
export const ENERGY_FLIGHT_EASE = 1.35;
/** The die stays for this long after the last coin drops, so the player reads the pips. */
export const ENERGY_FLIGHT_AFTER_MS = 450;
/** A coin in the air is this many times the size of a pip. It comes down to the size of the pip. */
export const ENERGY_COIN_FLIGHT_SCALE = 2.2;
/** The coin grows out of the die for this part of the flight, and shrinks into the pip for the last part. */
export const ENERGY_COIN_GROW_PORTION = 0.15;
export const ENERGY_COIN_SHRINK_PORTION = 0.35;
/** The size of a coin when it leaves the die, as a part of its size in the air. */
export const ENERGY_COIN_START_SCALE = 0.3;
/** The puffs behind a coin: how far behind each is, as a part of the flight, and its size as a part of the coin. */
export const ENERGY_PUFF_LAG = 0.07;
export const ENERGY_PUFF_SIZES = [0.7, 0.55, 0.4] as const;
/** The coin leaves the die this far over its center, as a part of the height of the die. */
export const ENERGY_COIN_SOURCE_LIFT = 0.25;
/**
 * The sparks over a slot a click casts from. Each spell has its own pattern,
 * drawn from its id: how many sparks, where each is across the face and in
 * the height of the aura, how many are large, and how long each waits
 * between two flashes. Garrett, 2026-10-12: "make it unique to each one so it
 * doesnt look so repetitive".
 */
export const AURA_SPARK_COUNT = [8, 13] as const;
/** Across the face, in percent. The arms of a large spark stay inside the face. */
export const AURA_SPARK_ACROSS = [8, 92] as const;
export const AURA_SPARK_BIG_SHARE = [0.25, 0.6] as const;
export const AURA_SPARK_TIME_MS = [800, 1900] as const;

/**
 * Energy a side pays leaves the pips as coins, from the last pip down. Each
 * coin leaves this long after the one before, and flies this long to the slot
 * of the skill it paid for.
 */
export const ENERGY_SPEND_STEP_MS = 70;
export const ENERGY_SPEND_FLIGHT_MS = 240;
/** The arc of a coin that a side pays. See `ENERGY_FLIGHT_BEND`. */
export const ENERGY_SPEND_BEND = [0.25, 0.45] as const;
/** A coin that a side pays is this many times the size of a pip in the air. */
export const ENERGY_SPEND_COIN_SCALE = 1.5;
/** A slot the side can pay for after a roll sounds this long after the last coin drops. */
export const SLOT_READY_SOUND_MS = 120;
/** lost-energy: the side gains no energy on this many turns of its own. */
export const LOST_ENERGY_TURNS = 2;
/** roll-discount: this many rolls of the side cost less, by this much. A roll can cost zero. */
export const ROLL_DISCOUNT_ROLLS = 2;
export const ROLL_DISCOUNT = 1;
/** checkers-energy. The cue names its card while the piece moves again. */
export const CAPTURE_AGAIN_SPELL_ID = 'checkers-energy';
/** clone-bud: the copy comes at the start of this turn of the owner, counted from the cast. */
export const CLONE_BUD_TURNS = 3;
/** forced-peace: no piece captures on the turn of the cast and on the next turn of the other side. */
export const FORCED_PEACE_PLIES = 2;
/**
 * Every roll costs this. Changed 2026-10-12. Rob: "let's tweak roll costs so
 * that they always cost 2 energy as a baseline". Until then the first roll
 * cost 1 and each roll cost 1 more than the last.
 */
export const ROLL_COST_FIRST = 2;
/** Each roll a side paid for adds this to the cost of its next one. Zero keeps the cost level. */
export const ROLL_COST_STEP = 0;

export const STOP_TURNS = 2;
export const UNCAPTURABLE_TURNS = 3;
export const KNIGHT_TURNS = 3;
export const EXTRA_ROLLS = 2;
/** The first turn of a match. A spell with no later turn of its own may be dealt from here. */
export const FIRST_TURN = 1;
/** Extra rolls stays off the wheel before this turn. Two more spells on turn one buried the opening. */
export const EXTRA_ROLLS_EARLIEST_TURN = 10;

// ---- AI --------------------------------------------------------------------
export const AI_TIME_BUDGET_MS = 650;
/**
 * Godsmode: the AI gives up when its search is this far behind at its move,
 * on `AI_CONCEDE_TURNS` of its turns in a row. Garrett, 2026-10-13, on that
 * rule with these two values: "Yeah do it". In 1000 matches of the AI
 * against the AI at depth 2, the rule ended 237, and the side that gave up
 * went on to win 9 of them. `npm run sim:endings -- --probe 4` counts it.
 */
export const AI_CONCEDE_LEAD = 2000;
export const AI_CONCEDE_TURNS = 3;
/** A side with less than the full search looks this deep for that score, with the usual score of a board. */
export const AI_CONCEDE_PROBE_DEPTH = 4;
export const AI_MAX_DEPTH = 7;
/**
 * suggest-best: the search for the suggestion gets this long. It is four
 * times the usual budget, which takes the search about one move deeper.
 */
export const AI_SUGGEST_BUDGET_MS = 2600;
/**
 * The hint for a player who moves by hand: the search stops at this depth.
 * It sees a capture and the reply to it, and no more. Rob, 2026-10-12: "an
 * \"OK move\" from the chess engine given the board", "provably worse in
 * quality than the Good Idea ability", and "It's allowed to be the same".
 * The search goes one depth after the other, so the search of suggest-best
 * starts with these same depths and then looks deeper. The hint is that
 * search, stopped early.
 */
export const AI_HINT_DEPTH = 2;
/** The depth the AI searches to with no piece that gives a pitch. It sees one move and no reply. */
export const AI_BLIND_DEPTH = 1;
/**
 * When the two pitches are the same move, the piece that gives the aggro
 * pitch offers a move of its own, from a search this deep in the aggro
 * style. It looks at the move and not at the reply. Rob, 2026-10-13: "the
 * aggro knight must recommend the most aggressive possible move for ITSELF
 * to make on the current board. It breaks ties by leaping into the breach."
 */
export const AI_OWN_AGGRO_DEPTH = 1;
/**
 * A pitch is an arrow on the board, from the piece to where it goes, in board
 * units: a band of half dither with a solid head, under the pieces. Changed
 * 2026-10-13. It was a solid shaft 9 wide on a dark edge of 4, over the
 * pieces. Garrett: "the stroke and width oft he arrows is also too heavy, I
 * want something that sits under the pieces". Two pitches that are the same
 * move are two bands side by side with one head, a part in each color. Rob,
 * 2026-10-13: "show their two colors superimposed at a slight offset in the
 * UI, so it's clear that it's both their recommendations". The bands are
 * centered on the line of the move. Until then the second arrow lay to the
 * right of the first, and the pair was off the middle of the square.
 */
export const PITCH_ARROW = {
  bandWidth: 12,
  /** From the middle of one band to the middle of the next, for pitches that are the same move. */
  laneGap: 14,
  headLength: 20,
  /** The head of one pitch. Each more pitch on the move adds `laneGap`. */
  headWidth: 26,
  /** The arrow stops this far outside the shape of a piece it would take. */
  clearance: 6,
} as const;
/**
 * What a role is worth to the side that has it, in the points of the
 * evaluation. The search and the aim of a spell read the value of a piece, so
 * these make both sides guard and hunt the pieces that carry a role. Rob,
 * 2026-10-13, on whether the engine should know these pieces matter:
 * "Absolutely yes".
 */
export const ROLE_VALUE = {
  /** A piece that carries a caster. Near the worth of a bishop on top of the piece. */
  caster: 300,
  /**
   * A piece that carries a caster while its side has a pawn with no caster.
   * The caster goes to that pawn when the piece is gone, so the side loses
   * the space and not the caster.
   */
  casterWithSpare: 60,
  /** Each point of room for energy on a piece, and each point it holds. */
  energyRoom: 40,
  energyPoint: 50,
  /** A piece that gives a pitch. */
  pitch: 200,
} as const;
/**
 * The aggro pitch. Its search scores a board for the side that asked: its own
 * pieces count for less, each rank a piece has gone forward counts, and each
 * piece near an enemy royal piece counts. Rob, 2026-10-13: "one *highly
 * aggressive* pitch". Rob agreed to "more credit for captures, advancing, and
 * pressure near the enemy's royal piece, and less weight on its own losses".
 */
export const AGGRO = {
  /** The part of the value of its own pieces that the aggro pitch counts. */
  ownWeight: 0.4,
  /** For each rank a piece of its own stands forward of its home rank. */
  advance: 22,
  /** For each piece of its own this near an enemy royal piece, in squares. */
  pressureReach: 2,
  pressure: 90,
} as const;
/**
 * The plan of a cast, see `ai/castPlan.ts`. Added 2026-10-13. Garrett: "I
 * would like for it to actually think strategyically about casts". Nobody
 * tuned these values.
 */
/** The search after each cast the AI tries looks this deep: its move and the reply. */
export const AI_CAST_PLAN_DEPTH = 2;
/** A search this deep weighs every cast first: one move, and the captures after it. */
export const AI_CAST_SCAN_DEPTH = 1;
/** The casts that did best in that first pass, which the search of full depth weighs. */
export const AI_CAST_PLAN_FINALISTS = 5;
/**
 * A skill out of its turn must win this much more, near the value of a
 * knight. The AI saves its energy for the skill whose turn it is, see
 * `ai/cast.ts`, and a cheap skill would take that energy on every turn.
 */
export const AI_CAST_OUT_OF_TURN_GAIN = 250;
/** The AI tries casts for this long at most. Then the best cast so far stands. */
export const AI_CAST_PLAN_BUDGET_MS = 500;
/** A skill is worth its cast when the search wins this much more for each energy, in the points of the evaluation. */
export const AI_CAST_GAIN_PER_ENERGY = 60;
/** A scroll is worth its cast when the search wins this much more. With no open scroll slot, any gain is enough. */
export const AI_CAST_SCROLL_GAIN = 100;
/** The AI does not take a skill in its turn when the search loses more than this with it. */
export const AI_CAST_VETO_LOSS = 150;
/** For a spell with two picks, the AI tries this many squares for the second pick. */
export const AI_CAST_SECOND_PICKS = 3;
/** For a cast that gives a random scroll, the AI tries this many draws. */
export const AI_CAST_DRAW_SAMPLES = 4;
export const AI_QUIESCENCE_DEPTH = 4;
export const AI_NODE_CHECK_INTERVAL = 2048;
/**
 * The AI does not pay for a roll when its best move wins this much or more,
 * in the points of the evaluation. It is the value of a pawn. Added
 * 2026-10-12. Rob: "moon should decline a roll if it has a raw chess move
 * available whose EV is above a certain threshold. Just take the good move
 * and bank the energy for a turn where it's less certain about the raw move."
 */
export const AI_KEEP_ENERGY_GAIN = 100;
/**
  After the board goes quiet, the AI waits this long before it acts. It was 600 until
  2026-10-11. Garrett, after the carry: "make the ai think/move slightly faster now that we
  actual animate the piece". The carry shows the move, so the beats around it are shorter:
  this one, the weigh, the pick, the lift, and the recap.
*/
export const AI_READ_DELAY_MS = 400;
/** One beat of the opening of a match: the casters of one side. Two play before the first roll. */
export const OPENING_BEAT_MS = 1100;

// ---- Stagecraft: how long each beat holds ----------------------------------
/** One knob for the whole tempo. 1.5 makes every beat half again as long. */
export const BEAT_PACE = 1.5;
/** The eligible targets light up while Violet weighs them. */
export const BEAT_CONSIDER_MS = 1100;
/** The chosen target holds before the spell lands. */
export const BEAT_PICK_MS = 850;
/** The effect line after a spell resolves, with the spark. */
export const BEAT_EFFECT_MS = 1800;
/** One step of a spell that moves pieces in steps. */
export const BEAT_STEP_MS = 1100;
/** A rule event: a wake, a captor removed, a promotion. Shorter than a cast. */
export const BEAT_EVENT_MS = 1300;
/** The carried piece hangs over its square, with its destination marked, before it travels. */
export const BEAT_LIFT_MS = 700;
/** The move line after the piece lands. */
export const BEAT_RECAP_MS = 700;
/** A spark's travel time from the source to a changed square. */
export const SPARK_TRAVEL_MS = 550;

// ---- The carry: how the AI moves a piece -----------------------------------
// Times are beat milliseconds, so `BEAT_PACE` scales them. A pair is a range.
// Each carry draws its own value from the range.
/** The piece comes up from its square. */
export const CARRY_LIFT_MS = 260;
/** The travel takes this long, plus the next value for each square of distance. */
export const CARRY_TRAVEL_BASE_MS = 260;
export const CARRY_TRAVEL_MS_PER_SQUARE = 45;
/** The piece comes down and settles. */
export const CARRY_LAND_MS = 300;
/** The part of the landing the piece takes to come down. The rest is the settle. */
export const CARRY_DROP_PORTION = 0.35;
/** Each carry is faster or slower by up to this ratio. */
export const CARRY_TEMPO_JITTER = 0.12;
/** The size of the piece while it is off the board. */
export const CARRY_LIFT_SCALE = [1.14, 1.24] as const;
/** How far up the page the piece comes, in board pixels. */
export const CARRY_RISE_PX = [8, 16] as const;
/** The most the piece leans into the travel, in degrees. */
export const CARRY_LEAN_DEGREES = [4, 9] as const;
/** A move along a file leans this much of the usual lean. */
export const CARRY_STRAIGHT_LEAN_RATIO = 0.5;
/** This part of the carries sway while the piece is held. The others hold still. */
export const CARRY_WOBBLE_CHANCE = 0.6;
/** How far the piece sways to each side, in degrees. */
export const CARRY_WOBBLE_DEGREES = [2.5, 6] as const;
/** One sway, there and back. */
export const CARRY_WOBBLE_MS = [420, 680] as const;
/** How far the path bends away from the straight line, in board pixels. */
export const CARRY_BOW_PX = [0, 14] as const;
/** How much the piece flattens when it is set down, as a part of its size. */
export const CARRY_SQUASH = [0.06, 0.12] as const;
/** The point of the piece that stays in place while it turns and flattens: the middle of its base. */
export const CARRY_PIVOT_PX = { x: 50, y: 84 } as const;
/** A captured piece starts to fade when the carried piece is this far through its travel. */
export const CARRY_CAPTURE_PORTION = 0.75;

// ---- Evaluation ------------------------------------------------------------
export const ROYAL_BONUS = 400;
export const EXTRA_LIFE_RATIO = 0.6;
export const PAWN_ADVANCE_BONUS = 9;
export const STOPPED_PENALTY_RATIO = 0.25;
/** How much of a piece's value the search writes off beside a piece about to explode. */
export const BLAST_NEIGHBOR_RATIO = 0.6;
export const MATE_SCORE = 100_000;
export const MATE_THRESHOLD = 50_000;

// ---- UI --------------------------------------------------------------------
/**
 * How long the die tumbles over the board before it lands. It was 2200 until
 * 2026-10-13. Rob: "try to have the roll animation take 60% as much time
 * before settling".
 */
export const DIE_SPIN_MS = 1320;
/** Faces the highlight visits on the wheel while the die spins. The last one is the roll. */
export const WHEEL_CYCLE_HOPS = 13;
/** The last hop takes this many times as long as the first. */
export const WHEEL_CYCLE_SLOWDOWN = 4;
/** How long the spell card stays on the stage after the die lands. */
export const STAGE_REVEAL_MS = 2600;
/**
 * How long the die stays after it lands on a face with no card: a roll for
 * energy. The player reads a number, not a card, so the hold is short.
 */
export const STAGE_ENERGY_HOLD_MS = 800;
/** A dragged card stays this many pixels inside the window. */
export const CARD_DRAG_MARGIN_PX = 8;
/** The card's flight from center stage to the hand. */
export const STAGE_LEAVE_MS = 450;
/** A captured piece, or a badge that ended, fades for this long before it leaves the page. */
export const PIECE_LEAVE_MS = 300;
/**
 * The cloud a removed piece leaves. The puffs start at the middle of the square
 * and go out. A puff is solid, then half dither, then a quarter, then gone.
 */
export const CLOUD_MS = 620;
/** The portions of `CLOUD_MS` at which a puff thins to half dither, and then to a quarter. */
export const CLOUD_THIN_PORTIONS = [0.4, 0.72] as const;
export const CLOUD_PUFF_COUNT = 7;
/** A band of a lasting effect in the board frame comes and goes in steps of dither, this long each. */
export const LASTING_BAND_STEP_MS = 110;
/**
 * A coin of energy that hits a seal on the line of Moon pings off it: it flies
 * this long, this many times the width of the seal to the side, and this many up.
 */
export const SEAL_PING_MS = 360;
export const SEAL_PING_REACH = 1.6;
export const SEAL_PING_LIFT = 1.2;
/**
 * The cover that closes over the board while the caster select is open. Each
 * square steps through a quarter dither, a half, and solid, `BOARD_COVER_STEP_MS`
 * for each step. The ring at the edge of the board goes first when it closes,
 * and the middle goes first when it opens. A ring starts `BOARD_COVER_RING_MS`
 * after the one before, and each square up to `BOARD_COVER_JITTER_MS` later.
 */
export const BOARD_COVER_STEP_MS = 70;
export const BOARD_COVER_RING_MS = 90;
export const BOARD_COVER_JITTER_MS = 60;
export const BOARD_COVER_RINGS = 4;
/** Three steps for each square: quarter, half, solid. */
export const BOARD_COVER_STEPS = 3;
export const BOARD_COVER_MS =
  (BOARD_COVER_RINGS - 1) * BOARD_COVER_RING_MS +
  BOARD_COVER_JITTER_MS +
  BOARD_COVER_STEPS * BOARD_COVER_STEP_MS;
/** How far a puff goes from the middle, and how large it gets, in board units. A pair is a range. */
export const CLOUD_PUFF_REACH_PX = [20, 34] as const;
export const CLOUD_PUFF_RADIUS_PX = [9, 16] as const;
/** The puff that stays on the middle. */
export const CLOUD_CORE_RADIUS_PX = 20;
/** The most a puff starts after the first one. */
export const CLOUD_PUFF_STAGGER_MS = 70;
/** New match asks once when a match is in progress. The question stays this long. */
export const NEW_MATCH_CONFIRM_MS = 3000;
/** The space between the name of a square and its top left corner, in board units. */
export const SQUARE_NAME_INSET_PX = 5;
/** A tooltip stays this far from its control and from the edge of the window. */
export const TOOLTIP_GAP_PX = 8;
/** A pointer must rest on a control this long before its tooltip shows. */
export const TOOLTIP_DELAY_MS = 280;
export const LOG_DISPLAY_LIMIT = 80;
export const LOG_ENTRY_HINT = 'Show the board at this line.';
export const LOG_TURN_LABEL = 'Turn';
/** Keys that move the history cursor when no form control has the focus. */
export const HISTORY_KEYS = {
  back: 'ArrowLeft',
  forward: 'ArrowRight',
  start: 'Home',
  live: 'End',
} as const;
/** The key that rolls the die when the roll is the player's. */
export const ROLL_KEY = ' ';
/** The key that puts the picked piece down. */
export const SELECTION_KEYS = { clear: 'Escape' } as const;
export const DEFAULT_SEED_MAX = 9999;
export const SPECTATE_QUERY_PARAM = 'spectate';
/** At this width or less, the page uses the compact layout: one screen, with a tray of tabs. */
export const COMPACT_LAYOUT_QUERY = '(max-width: 40rem)';

// ---- The tumbling die ------------------------------------------------------
/** How many walls the die may hit on one roll. Each roll picks one of these. */
export const TUMBLE_BOUNCE_COUNTS = [3, 4, 5] as const;
/** The die slows down along its path. A higher power spends more of the roll near the end. */
export const TUMBLE_EASE_POWER = 2.4;
/** Radians the die turns for each radius it travels. 1 is a ball that rolls with no slip. */
export const TUMBLE_ROLL_RATE = 0.8;
/** The most the turn axis leans out of the board plane after a wall, so the die also twists. */
export const TUMBLE_TWIST = 0.6;
/** How near the center of the die comes to a wall, in radii. */
export const TUMBLE_WALL_INSET = 0.85;
/** The die starts at this scale and grows to full size over this part of the roll. */
export const TUMBLE_START_SCALE = 0.35;
export const TUMBLE_GROW_PORTION = 0.12;
/** After a wall the die shrinks by this ratio and recovers over this part of the roll. */
export const TUMBLE_BONK_SQUASH = 0.12;
export const TUMBLE_BONK_PORTION = 0.05;
/** The radius of the die as a part of the stage width, when the layout has no slot for it. */
export const TUMBLE_FALLBACK_RADIUS_RATIO = 0.12;
/** One cell of the dither on the die, in screen pixels. A whole number keeps the cells sharp. */
export const TUMBLE_DITHER_CELL_PX = 3;
/** Where the light on the die comes from: left, up, and toward the viewer. */
export const TUMBLE_LIGHT = [-0.35, 0.45, 0.82] as const;
/** The darkest face keeps this many lit cells of sixteen, so it shows on a dark board. */
export const TUMBLE_AMBIENT_CELLS = 3;
/** The number on a face, as a part of the radius. */
export const TUMBLE_NUMBER_SIZE = 0.52;
/** A coin on a face of the die: its radius and, for two coins, how far each is from the middle. Parts of the number size. */
export const TUMBLE_COIN_RADIUS = 0.26;
export const TUMBLE_COIN_SPREAD = 0.34;
/** The mark of a side on a face of the coin flip die, and the band of light color around it. Parts of the number size. */
export const TUMBLE_SIDE_MARK_SIZE = 1.15;
export const TUMBLE_SIDE_MARK_BAND = 0.16;

// ---- The coin flip of a room ------------------------------------------------
/** The die of the flip rests this long after it lands, with the words of the side, before the opening. */
export const COIN_FLIP_REST_MS = 1600;

// ---- The die as the roll button ---------------------------------------------
/** The radius of the die as a part of half the button, so the glow fits around it. */
export const ROLL_DIE_RADIUS_RATIO = 0.78;
/** At rest the die is small, so it steps back until the next roll. */
export const ROLL_DIE_REST_RADIUS_RATIO = 0.6;
/** When it becomes the roll, the die starts with a turn at this speed, in radians a second. */
export const ROLL_DIE_WAKE_SPIN = 14;
/** How far the die leans back from the viewer, in radians, so three faces show. */
export const ROLL_DIE_TILT = 0.5;
/** While it waits for the roll, the die rocks this far to each side, in radians. */
export const ROLL_DIE_ROCK = 0.32;
/** One rock, there and back. */
export const ROLL_DIE_ROCK_MS = 2600;
/** Under the pointer the die turns at this speed, in radians a second. */
export const ROLL_DIE_HOVER_SPIN = 3.2;
/** How fast the turn speeds up and slows down. A higher value is more abrupt. */
export const ROLL_DIE_SPIN_EASE = 5;
/** The rings of dither around the die: the radius in die radii, and the lit cells of sixteen. */
export const ROLL_DIE_GLOW_RINGS = [
  { reach: 1.22, litCells: 9 },
  { reach: 1.42, litCells: 5 },
  { reach: 1.62, litCells: 2 },
] as const;
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

// ---- New spell rules ------------------------------------------------------
/** A piece a boardwide spell relocated misses one turn. */
export const MOVED_STOP_TURNS = 2;
/**
 * A piece its owner relocated with a spell cannot move for the rest of that
 * turn. The first tick comes at the start of the owner's next turn.
 */
export const RELOCATED_STOP_TURNS = 1;
/**
 * A piece of the caster that a spell changed cannot move for the rest of the
 * turn of the cast. The first tick comes at the start of the owner's next
 * turn, so the piece moves again then. Added 2026-10-12. Rob: "pieces that
 * are affected by spells should get \"spell sickness\"".
 * A piece of the other side has no stop from this. Until 2026-10-13 it was
 * stopped through the next turn of its owner. Rob: "if a spell affects one of
 * my units on my ENEMY'S turn, I should be able to act next turn. It's to
 * prevent the same piece who got a spell from moving THAT TURN, but not into
 * the next turn."
 */
export const CAST_STOP_TURNS_CASTER = 1;
/** The pieces of each side that trade squares. */
export const TRADE_PIECES = 3;
/** With fewer pieces than this, a side trades nothing. */
export const TRADE_PIECES_MIN = 2;
export const EXPLODE_TURNS = 3;
export const DORMANT_TURNS = 3;
export const PASS_THROUGH_TURNS = 3;
export const BACKWARD_TURNS = 3;
export const RANDOM_MOVE_TURNS = 3;
/**
 * Terrain lifetimes count plies, so both sides live through them. The deadly
 * square and the clone square last 2 turns since 2026-10-15. They lasted 4.
 * Rob: "Slight debuff: It lasts 2 turns", and "Same light debuff for Clone hole".
 */
export const DEADLY_PLIES = 4;
export const CLONE_PLIES = 4;
/** A multiple of every count of neighbors from 1 to 8, so each empty neighbor is as likely as the next. */
export const CLONE_SEED_RANGE = 840;
/** The side of the region a pick takes for a clone square. An odd count, so it has a middle. */
export const REGION_SIDE = 3;
/** A square that needs more moves than this is out of the reach of a side. */
export const REACH_MOVE_LIMIT = 3;
/** The id of a copy from a clone square until the flow gives it the next piece id. */
export const UNNUMBERED_PIECE_ID = 0;
export const SLOW_PLIES = 8;
export const WALL_PLIES = 4;
export const HISTORY_PLIES = 8;
/**
 * A match with this many passes in a row is a draw: neither side can move.
 * Without the limit, two sides with no move passed to each other with no end.
 */
export const PASS_LIMIT = 6;
/**
 * A match with this many plies in a row in which no piece came or went is a
 * draw. It is 25 turns of each side. In 1000 matches of the AI against the
 * AI, 25 had no end by ply 300, and this limit ends all 25. It also ends 1
 * match that a side won later. Keep the value even: the end line names half.
 */
export const QUIET_PLY_LIMIT = 50;
/** Events the state keeps for the narrator. */
export const EVENT_HISTORY = 12;
/**
 * When true, a fresh spell of the same polarity replaces a rolled face at the
 * end of the turn. Off since 2026-10-12, so a face keeps its spell for the
 * match. Rob: "A 2 is a 2 is a 2, until the next game, when there's a fresh draw."
 * A spell that answers another still takes its face and gives it back.
 */
export const WHEEL_REFRESH = false;
/**
 * When true, the menu has the Godsmode switch, and the player may start a
 * match with a wheel. Off since 2026-10-12, so every new match has casters.
 * Garrett: "lets just tuck away default mode for now and assume godsmode is
 * the default, as we then figure out the autochesser or not". The code of a
 * match with a wheel stays, and `npm run try` still opens one.
 */
export const WHEEL_MATCH_OFFERED = false;
/**
 * When true, a skill with `atHalfStrength` is locked until the army of its
 * caster falls to half strength. Off since 2026-10-12, so every skill is open
 * from the first turn. Garrett: "lets open up the third spell slot by default
 * ratrher than waiting for half game". The rule was Rob's, and its code stays.
 */
export const HALF_STRENGTH_LOCK = false;
/** The sacrifice of pawns takes this many pawns at least, and this many at most. */
export const SACRIFICE_GROUP_MIN = 2;
export const SACRIFICE_GROUP_MAX = 3;
/**
 * The weight of each kind in the draw of `change-kind`. Garrett, 2026-10-14:
 * "transmogrify needs balancing to make sure special pieces are a bit rarer".
 * The draw was even, so a queen or a king came 2 times in 5.
 */
export const CHANGE_KIND_WEIGHTS: Readonly<Record<string, number>> = {
  pawn: 4,
  knight: 3,
  bishop: 3,
  rook: 2,
  queen: 1,
  king: 1,
};

// ---- Scenarios: a match that arrives at a chosen spell, for a test ------------
/** A link with this parameter loads the match record in its value. */
export const MATCH_QUERY_PARAM = 'match';
/**
 * The roll may come this many turns after the turn asked for. A side that
 * rolls each time it can rolls on every second turn, and the AI keeps its
 * energy on some of those.
 */
export const SCENARIO_TURN_SPAN = 6;
/** How many seeds a search for a scenario tries. */
export const SCENARIO_SEED_TRIES = 400;
/** The depth of the AI in a scenario. A fixed depth gives the same match for the same seed. */
export const SCENARIO_SEARCH_DEPTH = 2;
/** Long, so the depth ends the search and the clock does not. */
export const SCENARIO_SEARCH_BUDGET_MS = 10_000;
export const SCENARIO_DEFAULT_TURN = 6;

// ---- The grain of the page ---------------------------------------------------
/** A cell of grain is a square of this side, the half of a dither cell. */
export const GRAIN_CELL_PX = 2;
/**
  The side of each tile, in cells. Two tiles lie over each other. The two sides
  share no factor, so the pair repeats only after 157 times 211 cells, more
  than 66,000px.
*/
export const GRAIN_TILE_CELLS = [157, 211] as const;
/** Marks on a tile for each 1,000 cells, before the mottle thins them. */
export const GRAIN_SPECKS_PER_1000 = 22;
export const GRAIN_FIBERS_PER_1000 = 2.4;
export const GRAIN_CLUMPS_PER_1000 = 0.9;
/** A fiber is a line of this many cells, least and most. */
export const GRAIN_FIBER_CELLS = [2, 6] as const;
/** One fiber in this many has a step of one cell at its middle. */
export const GRAIN_FIBER_BEND_ODDS = 3;
/** A clump is this many specks, least and most, within this many cells of its center. */
export const GRAIN_CLUMP_SPECKS = [3, 6] as const;
export const GRAIN_CLUMP_REACH_CELLS = 3;
/** The mottle is a grid of this many random values on a side. A mark stays with the chance at its place. */
export const GRAIN_MOTTLE_GRID = 5;
/** The least chance in the mottle, so no region of the page is bare. */
export const GRAIN_MOTTLE_FLOOR = 0.15;
/** The seed of the grain is a whole number under this. */
export const GRAIN_SEED_RANGE = 2 ** 31;

// ---- Sound -------------------------------------------------------------------
/** The samples of a sound are made at this rate. The browser plays them at its own. */
export const SOUND_SAMPLE_RATE = 48_000;
/** The level of every sound together, from 0 to 1. */
export const SOUND_MASTER_LEVEL = 0.6;
/** The loudest sample of a sound, by how much the moment asks for attention. */
export const SOUND_LEVEL = { quiet: 0.22, plain: 0.42, loud: 0.55 } as const;
/** The end of each sound fades for this long, so it does not click. */
export const SOUND_FADE_SECONDS = 0.004;
/** A sound that waits longer than this for the audio to start is dropped. */
export const SOUND_LATE_LIMIT_MS = 200;
/** The card comes up this long after the die lands, and its color sounds after that. */
export const SOUND_CARD_AFTER_LAND_MS = 150;
export const SOUND_POLARITY_AFTER_LAND_MS = 500;
/** A button with this attribute makes its own sound, so it gets no press sound. */
export const OWN_SOUND_ATTRIBUTE = 'data-own-sound';

// ---- Rooms -----------------------------------------------------------------
/** The letters of a room code: consonants with no look-alike, so no code spells a word. */
export const ROOM_CODE_LETTERS = 'BCDFGHJKMNPQRSTVWXZ';
export const ROOM_CODE_LENGTH = 4;
/** The server draws a new code this many times when it draws one in use. */
export const ROOM_CODE_TRIES = 8;
/** The path that makes a room. The socket of a room is under it. */
export const ROOMS_PATH = '/api/rooms';
/** A seat that is away this long lets the other seat claim the win. */
export const ROOM_CLAIM_AFTER_MS = 3 * 60 * 1000;
/** A room with no action for this long goes. */
export const ROOM_IDLE_EXPIRE_MS = 24 * 60 * 60 * 1000;
/** A room whose match is over goes after this long with no new match. */
export const ROOM_OVER_EXPIRE_MS = 60 * 60 * 1000;
/** The token of the seat of this browser in a room, by code. */
export const STORAGE_KEY_ROOM_TOKEN_PREFIX = 'arcane:room-token:';
/** The match of a room is saved apart from the match against the AI. */
export const STORAGE_KEY_ROOM_MATCH_PREFIX = 'arcane:room-match:';
/** A dropped socket tries again after this long, then twice as long each time, up to the most. */
export const ROOM_RECONNECT_FIRST_MS = 500;
export const ROOM_RECONNECT_MOST_MS = 8000;
/** The build of this page. A screen with another build than the server reloads. */
export const BUILD_ID: string = import.meta.env.VITE_BUILD_ID ?? 'dev';
